import { Link } from '@tanstack/react-router';
import type { AdminUserDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { formatEnergyFull } from '../../../shared/format-energy';
import {
  BOT_STATUS_LABEL,
  BOT_STATUS_VARIANT,
  botStatusOf,
  formatDateTime,
  formatUserName,
} from '../lib/format';

interface Props {
  user: AdminUserDto;
}

/** Верхний блок карточки: кто это, когда пришёл и откуда. */
export function UserSummary({ user }: Props) {
  const botStatus = botStatusOf(user);

  return (
    <div className="rounded-lg border bg-card p-6 space-y-3">
      <div>
        {user.username ? (
          <a
            href={`https://t.me/${user.username}`}
            target="_blank"
            rel="noreferrer"
            className="text-xl font-semibold underline-offset-2 hover:underline"
          >
            @{user.username}
          </a>
        ) : (
          <div className="text-xl font-semibold">—</div>
        )}
        <div className="text-muted-foreground">{formatUserName(user)}</div>
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Telegram ID</dt>
        <dd className="font-mono">{user.telegramId ?? '—'}</dd>
        <dt className="text-muted-foreground">Город</dt>
        <dd className="font-mono">{user.cityId ?? '—'}</dd>
        <dt className="text-muted-foreground">Premium</dt>
        <dd>{user.isPremium ? 'Да' : 'Нет'}</dd>
        <dt className="text-muted-foreground">Энергия</dt>
        <dd>{formatEnergyFull(user.energyAvailable)} ⚡</dd>
        <dt className="text-muted-foreground">Ивенты</dt>
        <dd>{user.meetingsCount}</dd>
        <dt className="text-muted-foreground">Связи</dt>
        <dd>{user.friendsCount ?? 0}</dd>
        <dt className="text-muted-foreground">Рефералы</dt>
        <dd>{user.referralsCount ?? 0}</dd>
        <dt className="text-muted-foreground">Last seen</dt>
        <dd>{formatDateTime(user.lastSeenAt)}</dd>
        <dt className="text-muted-foreground">Создан</dt>
        <dd>{formatDateTime(user.createdAt)}</dd>
        <dt className="text-muted-foreground">Бот</dt>
        <dd className="flex items-center gap-2">
          <Badge variant={BOT_STATUS_VARIANT[botStatus]}>{BOT_STATUS_LABEL[botStatus]}</Badge>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(user.botBlockedAt ?? user.botStartedAt)}
          </span>
        </dd>
        <dt className="text-muted-foreground">Пригласил</dt>
        <dd>
          {user.referredBy ? (
            <Link to="/users/$userId" params={{ userId: user.referredBy.id }} className="hover:underline">
              {user.referredBy.username ? `@${user.referredBy.username}` : (user.referredBy.firstName ?? '—')}
            </Link>
          ) : (
            '—'
          )}
        </dd>
      </dl>
      <Link
        to="/users"
        search={{ referredById: user.id }}
        className="text-sm text-muted-foreground hover:underline"
      >
        Кого пригласил →
      </Link>
    </div>
  );
}
